import { useCallback, useMemo, useState } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import type { MessagingPatient, MessagingTutorWithPets } from "../types";
import { patientService } from "../services/patientService";
import { GetRelatedPerson } from "@/services/fhir/relatedperson/GetRelatedPerson";
import { oids } from "@/configs/Settings";

type UseTutorLookupParams = {
  organizationId?: string;
};

type RelatedPersonBundleEntry = {
  resource?: {
    id?: string;
    patient?: { reference?: string };
    name?: Array<{ use?: string; text?: string }>;
    telecom?: Array<{ system?: string; value?: string }>;
    identifier?: Array<{ system?: string; value?: string }>;
  };
};

function pickPreferredNameText(name?: Array<{ use?: string; text?: string }>) {
  const list = name ?? [];
  const official = list.find((n) => n.use === "official")?.text?.trim();
  if (official) return official;
  const usual = list.find((n) => n.use === "usual")?.text?.trim();
  if (usual) return usual;
  return list[0]?.text?.trim() ?? "";
}

function normalizeSpaces(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

function normalizeDigits(value: string): string {
  return value.replace(/\D/g, "");
}

/**
 * CPF do tutor no FHIR: preferir identifier com system = OID BR-CPF (`oids.cpf`).
 * Se não existir, usa o primeiro `value` não vazio (compatível com bundles antigos).
 */
function pickTutorCpfFromIdentifiers(
  identifiers: Array<{ system?: string; value?: string }> | undefined,
): string {
  const list = identifiers ?? [];
  const byCpfOid = list.find(
    (row) => String(row.system ?? "").trim() === oids.cpf,
  );
  const preferred = String(byCpfOid?.value ?? "").trim();
  if (preferred) return preferred;
  for (const row of list) {
    const v = String(row.value ?? "").trim();
    if (v) return v;
  }
  return "";
}

function toTutorGroupKey(params: {
  cpf?: string;
  fullName: string;
  phone?: string;
}): string {
  const cpf = String(params.cpf ?? "").trim();
  if (cpf) return cpf;
  const nameKey = normalizeSpaces(params.fullName).toLowerCase();
  const phoneKey = normalizeDigits(String(params.phone ?? ""));
  return `name_phone_${nameKey}_${phoneKey}`;
}

function toPetIndex(
  patients: MessagingPatient[],
): Record<string, MessagingPatient> {
  const index: Record<string, MessagingPatient> = {};
  for (const p of patients) index[p.id] = p;
  return index;
}

/**
 * useTutorLookup
 *
 * Busca tutores (RelatedPerson) relacionados aos pets/pacientes disponíveis.
 * Reaproveita a chamada legada `GetRelatedPerson({ patient: idsCsv, vetRelated: true })`
 * e aplica agrupamento igual ao legado (prioriza CPF; fallback nome + telefone).
 */
export function useTutorLookup(params: UseTutorLookupParams = {}): {
  tutorsGrouped: MessagingTutorWithPets[];
  tutorsForSelection: MessagingPatient[];
  isLoading: boolean;
  hasError: boolean;
  load: () => Promise<void>;
} {
  const organizationId = params.organizationId;

  const [tutorsGrouped, setTutorsGrouped] = useState<MessagingTutorWithPets[]>(
    [],
  );
  const [tutorsForSelection, setTutorsForSelection] = useState<
    MessagingPatient[]
  >([]);

  const {
    run,
    loading: isLoading,
    error,
  } = useAsyncTask<[], void>({
    task: useCallback(async () => {
      if (!organizationId) {
        setTutorsGrouped([]);
        setTutorsForSelection([]);
        return;
      }

      const pets = await patientService.search({ term: "", organizationId });
      if (pets.length === 0) {
        setTutorsGrouped([]);
        setTutorsForSelection([]);
        return;
      }

      const petsIndex = toPetIndex(pets);
      const patientIdsParam = pets.map((p) => p.id).join(",");

      const response = await GetRelatedPerson({
        patient: patientIdsParam,
        vetRelated: true,
      });

      const entries = (response?.data?.entry ??
        []) as RelatedPersonBundleEntry[];

      // Agrupamento por CPF (ou fallback name+phone), mantendo lista de pets por tutor.
      const grouped: Record<string, MessagingTutorWithPets> = {};
      const selection: Record<string, MessagingPatient> = {};

      for (const entry of entries) {
        const rp = entry.resource;
        const relatedPersonId = String(rp?.id ?? "").trim();
        const patientRef = String(rp?.patient?.reference ?? "").trim();
        if (!relatedPersonId || !patientRef) continue;

        const petId = patientRef.replace(/^Patient\//, "");
        const pet = petsIndex[petId];
        if (!pet) continue;

        const fullName =
          normalizeSpaces(pickPreferredNameText(rp?.name)) || "-";
        const phone =
          rp?.telecom?.find((t) => t.system === "phone")?.value?.trim() ?? "";
        const cpf = pickTutorCpfFromIdentifiers(rp?.identifier).trim();

        const groupKey = toTutorGroupKey({ cpf, fullName, phone });
        if (!grouped[groupKey]) {
          grouped[groupKey] = {
            tutor: {
              id: relatedPersonId,
              fullName,
              cpf: cpf || undefined,
              phone: phone || undefined,
            },
            pets: [],
          };
        } else {
          // Complementa dados do tutor se vierem vazios no primeiro match.
          if (!grouped[groupKey].tutor.cpf && cpf)
            grouped[groupKey].tutor.cpf = cpf;
          if (!grouped[groupKey].tutor.phone && phone)
            grouped[groupKey].tutor.phone = phone;
        }

        const alreadyHasPet = grouped[groupKey].pets.some(
          (p) => p.id === pet.id,
        );
        if (!alreadyHasPet) {
          grouped[groupKey].pets.push({
            id: pet.id,
            fullName: pet.fullName,
            birthDate: pet.birthDate,
            cpf: pet.cpf,
          });
        }

        // Tutor -> MessagingPatient: FluxZap e tasks usam Patient lógico por CPF
        // (`messageSendService` com `forReference` ausente). Não enviar para RelatedPerson/{id}.
        const selectionKey = grouped[groupKey].tutor.id;
        if (!selection[selectionKey]) {
          selection[selectionKey] = {
            id: grouped[groupKey].tutor.id,
            fullName: grouped[groupKey].tutor.fullName,
            cpf: grouped[groupKey].tutor.cpf,
            phone: grouped[groupKey].tutor.phone,
            isTutor: true,
          };
        }
      }

      const groupedArray = Object.values(grouped);
      const selectionArray = Object.values(selection);

      setTutorsGrouped(groupedArray);
      setTutorsForSelection(selectionArray);
    }, [organizationId]),
  });

  const load = useCallback(async () => {
    await run();
  }, [run]);

  const hasError = useMemo(() => Boolean(error), [error]);

  return { tutorsGrouped, tutorsForSelection, isLoading, hasError, load };
}
